import { getScoreDataTypeIcon } from "@/src/features/scores/lib/scoreColumns";
import {
  type ScoreAggregate,
  type ScoreSimplified,
  type ScoreSourceType,
  type ScoreDomain,
  type ScoreDataTypeType,
  type ListableScore,
  type ListableScoreDataType,
} from "@langfuse/shared";

/**
 * Normalizes score names for comparison by converting - and . to _
 * "-" and "." reserved for splitting in namespace
 */
export const normalizeScoreName = (name: string): string => {
  return name.replaceAll(/[-\.]/g, "_");
};

export const composeAggregateScoreKey = ({
  name,
  source,
  dataType,
}: {
  name: string;
  source: ScoreSourceType;
  dataType: ScoreDataTypeType;
  keyPrefix?: string;
}): string => {
  const formattedName = normalizeScoreName(name);
  return `${formattedName}-${source}-${dataType}`;
};

export const decomposeAggregateScoreKey = (
  key: string,
): {
  name: string;
  source: ScoreSourceType;
  dataType: ScoreDataTypeType;
} => {
  const [name, source, dataType] = key.split("-");
  return {
    name,
    source: source as ScoreSourceType,
    dataType: dataType as ScoreDataTypeType,
  };
};

/**
 * How many values each score name carries across the aggregates handed in —
 * the counting sibling of `collectPresentScoreKeys`, which only answers
 * whether a score is present at all. Keyed by normalized score name and summed
 * across sources and data types, so a surface can prefer the best-recorded
 * score over whichever name sorts first. Client-side, from data already
 * fetched.
 */
export const collectScoreNameCoverage = (
  aggregates: (ScoreAggregate | null | undefined)[],
): Map<string, number> => {
  const coverage = new Map<string, number>();
  for (const aggregate of aggregates) {
    if (!aggregate) continue;
    for (const [key, value] of Object.entries(aggregate)) {
      const { name } = decomposeAggregateScoreKey(key);
      coverage.set(name, (coverage.get(name) ?? 0) + value.values.length);
    }
  }
  return coverage;
};

export const getScoreLabelFromKey = (key: string): string => {
  const { name, source, dataType } = decomposeAggregateScoreKey(key);
  return `${getScoreDataTypeIcon(dataType)} ${name} (${source.toLowerCase()})`;
};

export type ScoreToAggregate =
  | (Omit<ScoreDomain, "dataType"> & {
      dataType: ListableScore["dataType"];
      hasMetadata?: boolean;
    })
  | (ScoreSimplified & {
      hasMetadata?: boolean;
    });

/**
 * Display value of a boolean score. They are stored as 0/1 with "True"/"False"
 * string values, but users read and write them as booleans.
 */
export const toBooleanScoreValue = (score: {
  value?: number | null;
  stringValue?: string | null;
}): string =>
  score.value === 1 || score.stringValue === "True" ? "true" : "false";

/**
 * Maps score data types to aggregate types for processing.
 * Boolean scores are treated as categorical since they share the same
 * aggregation logic (value counting vs numeric averaging).
 */
const resolveAggregateType = (
  dataType: ListableScoreDataType,
): "NUMERIC" | "CATEGORICAL" => {
  return dataType === "NUMERIC" ? "NUMERIC" : "CATEGORICAL";
};

export const aggregateScores = <T extends ScoreToAggregate>(
  scores: T[],
): ScoreAggregate => {
  const groupedScores: Record<string, T[]> = scores.reduce(
    (acc, score) => {
      const key = composeAggregateScoreKey({
        name: score.name,
        source: score.source,
        dataType: score.dataType,
      });
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(score);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  // step 2: for each group, determine if the score is categorical or numeric & compute aggregate for group
  /* IMPORTANT
   * Some ScoreAggregates have a single value, and then include extra fields: comment, id, hasMetadata.
   * When the aggregate contains multiple values, these extra fields are undefined.
   */
  return Object.entries(groupedScores).reduce((acc, [key, scores]) => {
    const aggregateType = resolveAggregateType(scores[0].dataType);
    if (aggregateType === "NUMERIC") {
      const values = scores.map((score) => score.value ?? 0);
      if (!Boolean(values.length)) return acc;
      const average = values.reduce((a, b) => a + b, 0) / values.length;
      acc[key] = {
        type: aggregateType,
        values,
        average,
        comment: values.length === 1 ? scores[0].comment : undefined,
        executionTraceId:
          values.length === 1 ? scores[0].executionTraceId : undefined,
        id: values.length === 1 ? scores[0].id : undefined,
        hasMetadata: values.length === 1 ? scores[0].hasMetadata : undefined,
        timestamp: values.length === 1 ? scores[0].timestamp : undefined,
      };
    } else {
      const isBoolean = scores[0].dataType === "BOOLEAN";
      // Sorted by value: the score rows arrive in event-timestamp order, so an
      // unsorted cell lists "true, false" in one row and "false, true" in the
      // next, which makes a score column impossible to scan.
      const values = scores
        .map((score) =>
          isBoolean ? toBooleanScoreValue(score) : (score.stringValue ?? "n/a"),
        )
        .sort((a, b) => a.localeCompare(b));
      if (!Boolean(values.length)) return acc;
      const valueCounts = values.reduce(
        (acc, value) => {
          acc[value] = (acc[value] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
      acc[key] = {
        type: aggregateType,
        values,
        valueCounts: Object.entries(valueCounts).map(([value, count]) => ({
          value,
          count,
        })),
        comment: values.length === 1 ? scores[0].comment : undefined,
        executionTraceId:
          values.length === 1 ? scores[0].executionTraceId : undefined,
        id: values.length === 1 ? scores[0].id : undefined,
        hasMetadata: values.length === 1 ? scores[0].hasMetadata : undefined,
        timestamp: values.length === 1 ? scores[0].timestamp : undefined,
      };
    }
    return acc;
  }, {} as ScoreAggregate);
};
